import { SCULPT_MODES, sculptModeCss, type SculptMode } from '@/terrain/TerrainSculpt';
import { PLANT_TOOL_MODES, type PlantToolMode } from '@/editor/PlantBrush';
import { NAV_ACTIONS, type NavAction } from '@/rendering/CameraController';
import type { ActiveToolName } from './Toolbar';
import { el } from './dom';

/**
 * The pointer itself, drawn per tool.
 *
 * A cursor is the one piece of UI that is always where the user is looking, so it is the cheapest
 * place to say what the next click will do — the toolbar's pressed button is 40cm away from the
 * point of action, this is 0cm from it. Photoshop and SketchUp both do this; the wand is the case
 * that makes it obvious, because "click a thing and describe what you want" is unlike every other
 * click in the app.
 *
 * Two decisions worth recording:
 *
 * - It is a DOM element that follows the pointer rather than a `url(...)` CSS cursor. Native cursors
 *   cannot animate — browsers render one frame of an animated SVG and stop — and the motion is the
 *   point of half these glyphs (a wand that never twinkles is just a stick). The cost is one
 *   transform write per pointer move, coalesced into an animation frame.
 * - `select` deliberately has no glyph. The default arrow is what every user already knows means
 *   "nothing special happens here", and replacing it would spend the effect on the mode that needs
 *   it least. A null entry hands the canvas back its native cursor.
 *
 * Motion is kept under a couple of pixels and a couple of degrees, on slow loops: at 32px, a cursor
 * that moves as much as an icon would read as a fault rather than as life. It is switched off
 * wholesale under `prefers-reduced-motion` (see `cursor.css`).
 *
 * ## Sculpt draws four pointers, not one
 *
 * Raise, lower, smooth and flatten are one toolbar button and four different things to do to the
 * ground, and the choice between them is made in a panel on the far right and then held for minutes
 * at a time. A single "sculpt" pointer would leave the user's only reminder of which one is armed
 * across the screen from the ground they are about to move — and the cost of being wrong is a
 * gesture that has to be undone. So each mode gets its own drawing, in its own colour, and that
 * colour is the same one the brush ring wears (see `SCULPT_MODE_COLORS`).
 *
 * They are deliberately one family rather than four unrelated pictures: crosshair on the pointer,
 * ground profile below and to the right, and the mode said by what the ground is doing — heaped,
 * hollowed, wavy, or pressed level.
 *
 * ## Plant draws two pointers, for the same reason
 *
 * Placing one specimen and painting a drift are one tool with two behaviours (see `PlantBrush`), and
 * which one is armed is decided by the *plant* rather than by anything the user pressed — so the
 * pointer is the only place they can find out without trying it. One sprout means one click plants
 * one plant; a sprout with a scatter of seedlings behind it means a drag lays a bed.
 *
 * Both share the spot glyph's hotspot, at the base of the stem. Switching mode must not appear to
 * nudge the ground you are aiming at, which is the same rule the four sculpt pointers follow.
 *
 * ## Navigation borrows the pointer for as long as it lasts
 *
 * Orbit, pan and zoom are one button and a wheel (see `CameraController`), and which of them is
 * happening is otherwise invisible: `Shift` turns an orbit into a pan with no press and no release,
 * and the wheel has no button at all. So while the camera is moving, its move takes the pointer from
 * whatever tool is in hand, and hands it straight back when the move ends.
 *
 * These four are the one family here drawn in a colour no tool wears — a cool grey-blue — because
 * they are the one family that changes nothing in the garden. That is the reassurance a designer
 * mid-orbit actually wants: whatever the tool under this is, this drag is not planting anything.
 *
 * They are all centred on the hotspot, unlike the tool glyphs, because a camera move has no point of
 * action on the ground: it acts on the whole frame, and the pointer is only where it started.
 *
 * ## The note
 *
 * The pointer can also carry a line of text that fades after a few seconds ({@link ToolCursor.note}).
 * It exists because a glyph can say *which* mode without ever saying *how you got here* — a pointer
 * that silently changes shape when you pick a different plant is a puzzle rather than an
 * explanation. So a mode change says so in words, once, beside the drawing that just changed, and
 * then gets out of the way. It never persists: the status bar is where a standing answer belongs.
 */

/** Side of the square every glyph is drawn in, in CSS pixels. */
export const CURSOR_SIZE = 32;

export interface ToolCursorGlyph {
  /** Inner SVG markup, drawn in a `CURSOR_SIZE`-square viewBox. */
  readonly body: string;
  /** The point of the drawing that sits exactly under the pointer, in viewBox units. */
  readonly hotspot: readonly [number, number];
  /** Stroke colour for paths marked `class="ac"` — the one part of a glyph that is not white. */
  readonly accent: string;
  /** Short description of the pointer, for assistive tech and for the test that reads this table. */
  readonly label: string;
}

/**
 * What the pointer can be drawing: a tool, or — for sculpt and plant — a tool *and* its mode.
 *
 * A separate kind rather than an extra argument threaded through every call, so the one thing the
 * cursor is asked to show stays one value.
 */
export type CursorKind =
  | ActiveToolName
  | `sculpt-${SculptMode}`
  | `plant-${PlantToolMode}`
  | `nav-${NavAction}`;

/** The pointer for one sculpt mode. */
export function sculptCursorKind(mode: SculptMode): CursorKind {
  return `sculpt-${mode}`;
}

/** The pointer for one planting mode. */
export function plantCursorKind(mode: PlantToolMode): CursorKind {
  return `plant-${mode}`;
}

/** The pointer for one camera move. */
export function navCursorKind(action: NavAction): CursorKind {
  return `nav-${action}`;
}

/**
 * The crosshair every sculpt pointer wears, at the hotspot.
 *
 * Shared markup rather than four copies: it is the part that says *where*, and it has to be the
 * same pixel in all four or switching mode would appear to nudge the ground you are aiming at.
 */
const SCULPT_CROSS = '<path d="M6 1.8v8.4M1.8 6h8.4"/>';

/** One drawing per sculpt mode. See the note above on why these are a family. */
const SCULPT_GLYPHS: Record<SculptMode, ToolCursorGlyph> = {
  // Ground heaped into a mound, with the arrow that lifted it bobbing above. The mound holds still
  // so the motion reads as "this raises ground" rather than as unstable earth.
  raise: {
    body:
      SCULPT_CROSS +
      '<path d="M11 28.5h18"/>' +
      '<path d="M12.5 25c3.5-6.5 8-8 11-8s4.5 3.5 5.5 8"/>' +
      '<g class="lg-cursor__bob">' +
      '<path class="ac" d="M20.5 21V10"/>' +
      '<path class="ac" d="M20.5 9.5l-3.2 3.2M20.5 9.5l3.2 3.2"/>' +
      '</g>',
    hotspot: [6, 6],
    accent: sculptModeCss('raise'),
    label: 'Sculpt · raise',
  },

  // The exact inverse: the same ground scooped into a hollow, the same arrow pointing down into it.
  // Raise and lower are one operation with a sign, and the two pointers say so by being mirrors.
  lower: {
    body:
      SCULPT_CROSS +
      '<path d="M11 17c4 0 4 9 9 9s5-9 9-9"/>' +
      '<g class="lg-cursor__bob lg-cursor__bob--down">' +
      '<path class="ac" d="M20 9v11"/>' +
      '<path class="ac" d="M20 20.5l-3.2-3.2M20 20.5l3.2-3.2"/>' +
      '</g>',
    hotspot: [6, 6],
    accent: sculptModeCss('lower'),
    label: 'Sculpt · lower',
  },

  // Lumpy ground with a two-headed sweep passing over it — smoothing is a pass across a surface,
  // not a push at a point, so the glyph moves sideways where raise and lower move up and down.
  smooth: {
    body:
      SCULPT_CROSS +
      // Two humps written out rather than `q…t…`: a smooth-quadratic mirrors its control point, so
      // the second one would be a trough, and a trough here falls off the bottom of the box.
      '<path d="M11 26q4.5-8 9 0q4.5-8 9 0"/>' +
      '<g class="lg-cursor__sweep">' +
      '<path class="ac" d="M12.5 17h15"/>' +
      '<path class="ac" d="M12.5 17l3-2.6M12.5 17l3 2.6M27.5 17l-3-2.6M27.5 17l-3 2.6"/>' +
      '</g>',
    hotspot: [6, 6],
    accent: sculptModeCss('smooth'),
    label: 'Sculpt · smooth',
  },

  // A level plate pressing a bump down to a chosen height — the one mode that is about a *height*
  // rather than a direction, which is why it is a straight edge and not an arrow.
  flatten: {
    body:
      SCULPT_CROSS +
      '<path d="M11 26h3c2-6 8-6 10 0h5"/>' +
      '<g class="lg-cursor__press">' +
      '<path class="ac" d="M12 15h16"/>' +
      '<path class="ac" d="M17 15.5v3.4M23 15.5v3.4"/>' +
      '</g>',
    hotspot: [6, 6],
    accent: sculptModeCss('flatten'),
    label: 'Sculpt · flatten',
  },
};

/** The green both planting pointers wear, and the same one the placement ghost's ring is drawn in. */
const PLANT_ACCENT = '#8fd98c';

/** The ground line the sprout stands on. Its width is what says how much ground a press commits. */
const PLANT_GROUND_SPOT = '<path d="M11.5 29h9"/>';
const PLANT_GROUND_DRIFT = '<path d="M4 29h24"/>';

/** One drawing per planting mode. See the note above on why there are two. */
const PLANT_GLYPHS: Record<PlantToolMode, ToolCursorGlyph> = {
  // A sprout standing on the pointer: the plant goes where its stem meets the ground, so the stem
  // base *is* the hotspot rather than a crosshair standing in for one. Full height and alone in the
  // box, because that is the claim — one press, one plant, exactly here.
  spot: {
    body:
      '<g class="lg-cursor__sway">' +
      '<path d="M16 28.5V18.5"/>' +
      '<path class="ac lg-cursor__leaf-r" d="M16 18.7c0-5.3 4-9.3 9.3-9.3 0 5.3-4 9.3-9.3 9.3z"/>' +
      '<path class="ac lg-cursor__leaf-l" d="M16 18.7c0-5.3-4-9.3-9.3-9.3 0 5.3 4 9.3 9.3 9.3z"/>' +
      '</g>' +
      PLANT_GROUND_SPOT,
    hotspot: [16, 29],
    accent: PLANT_ACCENT,
    label: 'Plant · place one',
  },

  // The same sprout, shorter, with a seedling either side of it on a ground line that now runs the
  // width of the box. The two flankers fade in and out on a stagger — the drift filling in as you
  // drag — while the middle one holds, so the pointer still has a fixed thing to aim with.
  paint: {
    body:
      '<g class="lg-cursor__sway">' +
      '<path d="M16 28.5V22.5"/>' +
      '<path class="ac lg-cursor__leaf-r" d="M16 22.7c0-3.6 2.7-6.3 6.3-6.3 0 3.6-2.7 6.3-6.3 6.3z"/>' +
      '<path class="ac lg-cursor__leaf-l" d="M16 22.7c0-3.6-2.7-6.3-6.3-6.3 0 3.6 2.7 6.3 6.3 6.3z"/>' +
      '</g>' +
      '<g class="lg-cursor__seed">' +
      '<path d="M7.5 28.5v-2.7"/>' +
      '<path class="ac" d="M7.5 25.8c0-2 1.5-3.5 3.5-3.5 0 2-1.5 3.5-3.5 3.5z"/>' +
      '</g>' +
      '<g class="lg-cursor__seed lg-cursor__seed--b">' +
      '<path d="M24.5 28.5v-3.6"/>' +
      '<path class="ac" d="M24.5 24.9c0-2-1.5-3.5-3.5-3.5 0 2 1.5 3.5 3.5 3.5z"/>' +
      '</g>' +
      PLANT_GROUND_DRIFT,
    hotspot: [16, 29],
    accent: PLANT_ACCENT,
    label: 'Plant · paint a drift',
  },
};

/**
 * The one hue no tool wears: navigation changes the view and nothing in the garden, and the colour
 * is what says so before the shape has even been read.
 */
const NAV_ACCENT = '#9dc0dc';

/** One drawing per camera move. See the note above on why navigation takes the pointer at all. */
const NAV_GLYPHS: Record<NavAction, ToolCursorGlyph> = {
  // A wireframe globe: equator fixed, meridian turning. The turn is the whole message — orbit is
  // the one move where the *site* appears to rotate — so the meridian is the accent and the only
  // part that moves, and it swings rather than spinning, since a spinning cursor reads as "busy".
  orbit: {
    body:
      '<circle cx="16" cy="16" r="8.6"/>' +
      '<ellipse cx="16" cy="16" rx="8.6" ry="3.5"/>' +
      '<ellipse class="ac lg-cursor__spin" cx="16" cy="16" rx="3.5" ry="8.6"/>',
    hotspot: [16, 16],
    accent: NAV_ACCENT,
    label: 'Orbit',
  },

  // The hand every map and every drawing tool pans with, drifting a pixel or so as if it had hold of
  // the site. No accent part: unlike the other three, there is no piece of this that means something
  // the rest of it does not — the hand is the whole gesture. Deliberately *not* the four-way arrow,
  // which the move tool owns; dragging the view and dragging a plant must not look alike.
  pan: {
    body:
      '<g class="lg-cursor__drift">' +
      '<path d="M12.4 18.6V9.2a2 2 0 0 1 4 0v6.4"/>' +
      '<path d="M16.4 15.6V8a2 2 0 0 1 4 0v7.6"/>' +
      '<path d="M20.4 15.6V9.8a2 2 0 0 1 4 0v9.8a8 8 0 0 1-8 8h-2.2a7 7 0 0 1-5-2.1l-4.3-4.3' +
      'a2 2 0 0 1 2.9-2.8l4.6 4.3"/>' +
      '</g>',
    hotspot: [16, 16],
    accent: NAV_ACCENT,
    label: 'Pan',
  },

  // The magnifier, because zoom is the one camera move with a picture everyone already knows. The
  // sign is the accent: it is the single stroke that separates the two, so it must be the piece the
  // eye lands on. The lens swells as the view comes in, and shrinks as it goes out — the handle
  // stays put, so the motion reads as the lens changing rather than the whole tool sliding.
  'zoom-in': {
    body:
      '<g class="lg-cursor__lens">' +
      '<circle cx="16" cy="16" r="7.6"/>' +
      '<path class="ac" d="M16 12.2v7.6M12.2 16h7.6"/>' +
      '</g>' +
      '<path d="M21.4 21.4L27 27"/>',
    hotspot: [16, 16],
    accent: NAV_ACCENT,
    label: 'Zoom in',
  },

  'zoom-out': {
    body:
      '<g class="lg-cursor__lens lg-cursor__lens--out">' +
      '<circle cx="16" cy="16" r="7.6"/>' +
      '<path class="ac" d="M12.2 16h7.6"/>' +
      '</g>' +
      '<path d="M21.4 21.4L27 27"/>',
    hotspot: [16, 16],
    accent: NAV_ACCENT,
    label: 'Zoom out',
  },
};

/** White stroke throughout; `ac` marks the accent stroke, and `sp`/`bob`/… drive the animations. */
const GLYPHS: Record<ActiveToolName, ToolCursorGlyph | null> = {
  // The default arrow already says "select". See the note above.
  select: null,

  // Like sculpt below: the tool with no mode named yet draws the one it most often arms in, rather
  // than a third generic picture the user would have to unlearn on their first groundcover.
  plant: PLANT_GLYPHS.spot,

  // Four-way arrow centred on the pointer — the axis cross is the drag itself, so it has no offset
  // glyph. The heads breathe outward, which is the drag it is inviting.
  move: {
    body:
      '<path d="M16 5v22M5 16h22"/>' +
      '<g class="lg-cursor__breathe">' +
      '<path d="M16 4l-3.4 3.4M16 4l3.4 3.4M16 28l-3.4-3.4M16 28l3.4-3.4"/>' +
      '<path d="M4 16l3.4-3.4M4 16l3.4 3.4M28 16l-3.4-3.4M28 16l-3.4 3.4"/>' +
      '</g>',
    hotspot: [16, 16],
    accent: '#e9edf2',
    label: 'Move',
  },

  // The turn arrow rocks a few degrees rather than spinning: a spinning cursor reads as "busy".
  rotate: {
    body:
      '<g class="lg-cursor__rock">' +
      '<path d="M26.7 16a10.7 10.7 0 1 1-4-8.3"/>' +
      '<path class="ac" d="M26.7 5.3V12h-6.7"/>' +
      '</g>' +
      '<path d="M16 15.2v1.6"/>',
    hotspot: [16, 16],
    accent: '#8fd98c',
    label: 'Rotate',
  },

  // Two plants, one grown — dragging ages the plant, so the tall one is the one that moves.
  maturity: {
    body:
      '<path d="M5.5 27h21"/>' +
      '<path d="M11 27V15"/>' +
      '<path class="ac lg-cursor__grow" d="M21.5 27V7"/>' +
      '<path class="ac lg-cursor__grow" d="M21.5 6.5l-3.4 3.4M21.5 6.5l3.4 3.4"/>' +
      '<path d="M11 14.6l-2.8 2.8M11 14.6l2.8 2.8"/>',
    hotspot: [16, 16],
    accent: '#8fd98c',
    label: 'Maturity',
  },

  // A loaded brush pointing down-left, tip on the pointer, with a bead of colour at the tip. The
  // ground ring shows the brush *size*; this says which brush is in hand.
  paint: {
    body:
      '<g class="lg-cursor__wobble">' +
      '<path d="M15 12.6l4.4 4.4 8.1-8.1a3.1 3.1 0 0 0-4.4-4.4z"/>' +
      '<path d="M12.6 15l4.4 4.4"/>' +
      '<path d="M11.4 16.2l4.4 4.4-6.2 6.2-5.2 1 1-5.2z"/>' +
      '<circle class="ac lg-cursor__bead" cx="5" cy="27" r="1.6"/>' +
      '</g>',
    hotspot: [4.6, 27.4],
    accent: '#8fd98c',
    label: 'Paint ground',
  },

  // The tool with no mode named yet draws the mode it starts in, rather than a fifth generic
  // picture the user would then have to unlearn the moment the panel says "Raise".
  sculpt: SCULPT_GLYPHS.raise,

  // The wand: handle back to the lower right, business end on the pointer. Gold, to match the ring
  // the wand drops on the ground when it takes aim.
  wand: {
    body:
      '<g class="lg-cursor__tilt">' +
      '<path d="M26.5 26.5L12 12"/>' +
      '<path d="M21.5 21.5l2.5-2.5"/>' +
      '<path class="ac lg-cursor__spark" d="M7.5 1l1.6 4.9L14 7.5l-4.9 1.6L7.5 14l-1.6-4.9L1 7.5l4.9-1.6z"/>' +
      '<path class="ac lg-cursor__spark lg-cursor__spark--b" d="M17.5 3l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2L14.5 6l2.2-.8z"/>' +
      '<path class="ac lg-cursor__spark lg-cursor__spark--c" d="M4 17.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2L1 20.5l2.2-.8z"/>' +
      '</g>',
    hotspot: [7.5, 7.5],
    accent: '#ffd166',
    label: 'Magic wand',
  },

  // A luggage tag hanging from the pointer by its corner, swinging as if it had just been hung
  // there. The corner is the hotspot because that is the end you pin to a thing.
  tag: {
    body:
      '<g class="lg-cursor__swing">' +
      '<path d="M8 8h11.2l8.8 8.8-11.2 11.2L8 19.2z"/>' +
      '<path class="ac" d="M13.4 13.4v.02"/>' +
      '</g>',
    hotspot: [8, 8],
    accent: '#8fd98c',
    label: 'Tag meaning',
  },
};

/** The glyph for a pointer kind, or `null` where the platform's own arrow is the right answer. */
export function cursorGlyph(kind: CursorKind): ToolCursorGlyph | null {
  if (kind.startsWith('sculpt-')) {
    const mode = modeOf(kind, 'sculpt-', SCULPT_MODES);
    return mode ? SCULPT_GLYPHS[mode] : null;
  }
  if (kind.startsWith('plant-')) {
    const mode = modeOf(kind, 'plant-', PLANT_TOOL_MODES);
    return mode ? PLANT_GLYPHS[mode] : null;
  }
  if (kind.startsWith('nav-')) {
    const action = modeOf(kind, 'nav-', NAV_ACTIONS);
    return action ? NAV_GLYPHS[action] : null;
  }
  return GLYPHS[kind as ActiveToolName] ?? null;
}

/**
 * The mode a `tool-mode` kind names, or `null` when it names one this build does not have.
 *
 * Null rather than a fallback glyph: a kind the table does not know is a bug in the caller, and
 * quietly drawing the first mode instead would hide it behind a pointer that looks plausible.
 */
function modeOf<T extends string>(kind: string, prefix: string, modes: readonly T[]): T | null {
  const mode = kind.slice(prefix.length) as T;
  return modes.includes(mode) ? mode : null;
}

/** Every tool that draws its own pointer. */
export function customCursorTools(): ActiveToolName[] {
  return (Object.keys(GLYPHS) as ActiveToolName[]).filter((tool) => GLYPHS[tool] !== null);
}

/** Every per-mode sculpt pointer, in panel order. */
export function sculptCursorKinds(): CursorKind[] {
  return SCULPT_MODES.map(sculptCursorKind);
}

/** Every per-mode planting pointer. */
export function plantCursorKinds(): CursorKind[] {
  return PLANT_TOOL_MODES.map(plantCursorKind);
}

/** Every camera-move pointer, in the order the navigation scheme is documented. */
export function navCursorKinds(): CursorKind[] {
  return NAV_ACTIONS.map(navCursorKind);
}

/** Render a glyph as a standalone SVG string. Exported for the toolbar tooltip preview and tests. */
export function cursorSvg(glyph: ToolCursorGlyph): string {
  return (
    `<svg viewBox="0 0 ${CURSOR_SIZE} ${CURSOR_SIZE}" width="${CURSOR_SIZE}" height="${CURSOR_SIZE}" ` +
    `fill="none" aria-hidden="true" stroke="#ffffff" stroke-width="1.8" stroke-linecap="round" ` +
    `stroke-linejoin="round">${glyph.body}</svg>`
  );
}

export interface ToolCursorDeps {
  /** Full-viewport, pointer-transparent element the glyph is positioned inside (the HUD root). */
  host: HTMLElement;
  /** The element whose pointer is being tracked — the WebGL canvas. */
  canvas: HTMLElement;
}

/** How long a note stays up before it starts to fade, in milliseconds. */
export const CURSOR_NOTE_HOLD_MS = 3000;

/** Length of the note's fade, matching the transition in `cursor.css`. */
const NOTE_FADE_MS = 300;

/**
 * How close to the right edge the pointer has to be before the note is drawn on its other side.
 *
 * Generous, because the cost of guessing wrong in the safe direction is a note a little left of
 * where it usually is, and the cost of guessing wrong in the other is a note running off-screen.
 */
const NOTE_FLIP_MARGIN = 300;

export class ToolCursor {
  readonly root: HTMLElement;

  /** The glyph's own box. Separate from `root` so a re-render cannot blow away the note beside it. */
  private readonly glyph: HTMLElement;
  private readonly noteEl: HTMLElement;

  private kind: CursorKind = 'select';
  private enabled = true;
  private inside = false;
  private clientX = 0;
  private clientY = 0;
  private frameHandle: number | null = null;
  private noteTimer: ReturnType<typeof setTimeout> | null = null;
  private noteClearTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly onPointerMove = (e: PointerEvent): void => {
    this.clientX = e.clientX;
    this.clientY = e.clientY;
    // A mouse that never left the canvas can still arrive here first after a tool switch.
    this.inside = true;
    this.requestPlace();
  };
  private readonly onPointerEnter = (e: PointerEvent): void => this.onPointerMove(e);
  private readonly onPointerLeave = (): void => {
    this.inside = false;
    this.applyVisibility();
  };
  // Pressed while the button is down, released even if the mouse came up over a panel.
  private readonly onPointerDown = (): void => this.root.classList.add('is-pressed');
  private readonly onPointerUp = (): void => this.root.classList.remove('is-pressed');

  constructor(private readonly deps: ToolCursorDeps) {
    // The drawing is decorative and is hidden; the note is not. `aria-hidden` therefore sits on the
    // glyph rather than on the wrapper, because it hides everything beneath it and a mode change
    // announced only in pixels is announced to only some people.
    this.glyph = el('div', { class: 'lg-cursor__glyph', attrs: { 'aria-hidden': 'true' } });
    // Polite: an aside about the tool in hand, not something that has gone wrong.
    this.noteEl = el('div', {
      class: 'lg-cursor__note',
      attrs: { role: 'status', 'aria-live': 'polite' },
    });
    this.root = el('div', { class: 'lg-cursor' }, this.glyph, this.noteEl);
    deps.host.appendChild(this.root);

    deps.canvas.addEventListener('pointermove', this.onPointerMove);
    deps.canvas.addEventListener('pointerenter', this.onPointerEnter);
    deps.canvas.addEventListener('pointerleave', this.onPointerLeave);
    deps.canvas.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);

    this.render();
  }

  /** Point at the tool now in hand. Cheap to call every HUD refresh — a repeat is a no-op. */
  setTool(kind: CursorKind): void {
    if (kind === this.kind) return;
    this.kind = kind;
    this.render();
  }

  /**
   * Switch the whole thing off, restoring the canvas's own cursor. Photo mode uses this: the HUD is
   * hidden there, and a hidden HUD must not leave the canvas with no pointer at all.
   */
  setEnabled(enabled: boolean): void {
    if (enabled === this.enabled) return;
    this.enabled = enabled;
    if (!enabled) this.note('');
    this.applyVisibility();
  }

  /**
   * Say one short thing beside the pointer, then take it back.
   *
   * Re-noting replaces whatever was up and restarts the clock, rather than queueing: two mode
   * changes in a second are one event from the user's side, and the second message is the true one.
   * An empty string clears it at once — what a tool being put down wants.
   */
  note(text: string, holdMs = CURSOR_NOTE_HOLD_MS): void {
    this.clearNoteTimers();
    if (text === '') {
      this.noteEl.classList.remove('is-visible');
      this.noteEl.textContent = '';
      return;
    }
    this.noteEl.textContent = text;
    this.noteEl.classList.add('is-visible');
    this.noteTimer = setTimeout(() => {
      this.noteTimer = null;
      this.noteEl.classList.remove('is-visible');
      // Emptied only once the fade has finished, so the text is not yanked out mid-transition — and
      // so it leaves the accessibility tree instead of lingering as an invisible announcement.
      this.noteClearTimer = setTimeout(() => {
        this.noteClearTimer = null;
        this.noteEl.textContent = '';
      }, NOTE_FADE_MS);
    }, Math.max(0, holdMs));
  }

  private clearNoteTimers(): void {
    if (this.noteTimer !== null) clearTimeout(this.noteTimer);
    if (this.noteClearTimer !== null) clearTimeout(this.noteClearTimer);
    this.noteTimer = null;
    this.noteClearTimer = null;
  }

  private render(): void {
    const glyph = cursorGlyph(this.kind);
    this.root.dataset['tool'] = this.kind;
    this.glyph.innerHTML = glyph ? cursorSvg(glyph) : '';
    this.root.style.setProperty('--lg-cursor-accent', glyph?.accent ?? 'transparent');
    // The press dip scales about the hotspot, so the acting point stays put under the mouse.
    this.root.style.setProperty('--lg-cursor-hx', `${glyph?.hotspot[0] ?? CURSOR_SIZE / 2}px`);
    this.root.style.setProperty('--lg-cursor-hy', `${glyph?.hotspot[1] ?? CURSOR_SIZE / 2}px`);
    this.applyVisibility();
    this.place(glyph);
  }

  /**
   * Hide the glyph — and give the canvas its native cursor back — whenever it should not show.
   *
   * Two flags rather than one, because the note and the drawing do not appear under the same
   * conditions: the drawing shows only for a tool that has one, while the note shows for any tool
   * whose pointer is over the canvas. `select` draws nothing and can still have something to say.
   */
  private applyVisibility(): void {
    const glyph = cursorGlyph(this.kind);
    const owns = this.enabled && glyph !== null;
    this.deps.canvas.style.cursor = owns ? 'none' : '';
    this.root.classList.toggle('is-visible', owns && this.inside);
    this.root.classList.toggle('is-tracking', this.enabled && this.inside);
    if (!owns) this.root.classList.remove('is-pressed');
  }

  private requestPlace(): void {
    if (!this.enabled) return;
    if (!this.root.classList.contains('is-tracking')) this.applyVisibility();
    if (this.frameHandle !== null) return;
    const schedule =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame
        : (cb: FrameRequestCallback): number => setTimeout(() => cb(0), 16) as unknown as number;
    this.frameHandle = schedule(() => {
      this.frameHandle = null;
      this.place(cursorGlyph(this.kind));
    });
  }

  private place(glyph: ToolCursorGlyph | null): void {
    // The host is fixed to the viewport, so client coordinates are already its coordinates.
    const x = this.clientX - (glyph?.hotspot[0] ?? CURSOR_SIZE / 2);
    const y = this.clientY - (glyph?.hotspot[1] ?? CURSOR_SIZE / 2);
    this.root.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    // The note reads to the right of the pointer, except where there is no room to its right.
    const width = typeof window === 'undefined' ? 0 : window.innerWidth;
    this.root.classList.toggle('is-flipped', width > 0 && this.clientX > width - NOTE_FLIP_MARGIN);
  }

  dispose(): void {
    if (this.frameHandle !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.frameHandle);
    }
    this.frameHandle = null;
    this.clearNoteTimers();
    this.deps.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.deps.canvas.removeEventListener('pointerenter', this.onPointerEnter);
    this.deps.canvas.removeEventListener('pointerleave', this.onPointerLeave);
    this.deps.canvas.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    this.deps.canvas.style.cursor = '';
    this.root.remove();
  }
}
